import { useEffect, useState } from "react";
import { Heart, Check, ArrowRight, MoreVertical, Sparkles, RefreshCcw } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import AvatarMedia from "@/components/avatar/AvatarMedia";
import {
  matrizFirst,
  consciousnessLabel,
  avatarFooterLine,
  publicAvatarDescription,
} from "@/lib/avatarUi";
import { countAvatarFields, AVATAR_REQUIRED_FIELDS } from "@/lib/avatarFlow";

// Reference visual 1 (FR24 / Story 5.3). Structure 1–6 from §UI of the PRD.
// States: default / favorito / draft (60% + x/12, no CTA) / sem-foto (monograma).
// `compact` renders the horizontal picker variant used by the AgentChat gate.
export default function AvatarCardStrategy({
  avatar,
  onWriteCopy,
  onFavorite,
  onGeneratePhoto,
  onRegeneratePhoto,
  generating = false,
  compact = false,
  selectable = false,
  selected = false,
  onSelect,
  readOnly = false,
  peerAvatars,
}) {
  const [fav, setFav] = useState(Boolean(avatar.favorito));
  useEffect(() => setFav(Boolean(avatar.favorito)), [avatar.favorito]);

  const isDraft = avatar.status !== "complete";
  const objetivo = matrizFirst(avatar, "desejos", "desejos");
  const dor = matrizFirst(avatar, "dores", "dores");
  const gatilho = avatar.gatilho_compra;
  const consciencia = consciousnessLabel(avatar.nivel_consciencia);
  const footer = avatarFooterLine(avatar);
  const publicDescription = publicAvatarDescription(avatar, peerAvatars);

  const toggleFavorite = async (e) => {
    e.stopPropagation();
    const next = !fav;
    setFav(next); // optimistic
    try {
      await onFavorite?.(avatar, next);
    } catch {
      setFav(!next); // rollback
    }
  };

  // --- Compact (picker) variant -------------------------------------------
  if (compact) {
    return (
      <button
        type="button"
        data-testid="avatar-card-strategy"
        onClick={() => onSelect?.(avatar)}
        className={`w-full flex items-center gap-3.5 text-left rounded-2xl border p-3 transition-colors min-h-[44px] ${
          selected
            ? "border-brand-rose bg-brand-rose/5"
            : "border-brand-line bg-brand-elevated hover:border-brand-rose"
        }`}
      >
        <AvatarMedia
          avatar={avatar}
          peerAvatars={peerAvatars}
          aspectClass="aspect-square"
          roundedClass="rounded-xl"
          monogramClass="text-2xl"
          className="w-16 shrink-0"
          testid="avatar-card-strategy-media"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="font-display text-lg text-brand-ink truncate">{publicDescription}</span>
            {!isDraft && (
              <span className="shrink-0 w-4 h-4 rounded-full bg-brand-rose flex items-center justify-center">
                <Check className="w-2.5 h-2.5 text-white" />
              </span>
            )}
          </div>
          {objetivo && (
            <div className="text-[12px] text-brand-ink3 truncate mt-0.5">
              <span className="text-brand-rose">objetivo</span> · {objetivo}
            </div>
          )}
        </div>
        {selected && <Check className="w-4 h-4 text-brand-rose shrink-0" />}
      </button>
    );
  }

  // --- Full card -----------------------------------------------------------
  const Wrapper = selectable ? "button" : "div";
  const wrapperProps = selectable
    ? { type: "button", onClick: () => onSelect?.(avatar) }
    : {};

  return (
    <Wrapper
      {...wrapperProps}
      data-testid="avatar-card-strategy"
      className={`relative flex flex-col text-left rounded-2xl bg-brand-surface border p-4 transition-colors w-full ${
        selected ? "border-brand-rose" : "border-brand-line hover:border-brand-rose"
      } ${isDraft ? "opacity-60" : ""}`}
    >
      {/* 1. Category tag + favorite / draft progress */}
      <div className="flex items-center justify-between gap-2 mb-3">
        {avatar.nicho ? (
          <span
            data-testid="avatar-card-nicho"
            className="px-2.5 py-1 rounded-full bg-brand-elevated text-[11px] font-ui text-brand-ink2 lowercase truncate max-w-[70%]"
          >
            {avatar.nicho}
          </span>
        ) : (
          <span />
        )}
        {isDraft ? (
          <span className="text-[11px] font-ui text-brand-rose shrink-0">
            {countAvatarFields(avatar)}/{AVATAR_REQUIRED_FIELDS.length}
          </span>
        ) : readOnly ? (
          <span />
        ) : (
          <button
            type="button"
            data-testid="avatar-card-favorite"
            onClick={toggleFavorite}
            aria-pressed={fav}
            aria-label={fav ? "Remover dos favoritos" : "Favoritar"}
            className="shrink-0 w-9 h-9 -mr-1 -mt-1 flex items-center justify-center rounded-full text-brand-ink2 hover:text-brand-rose transition-colors"
          >
            <Heart className="w-5 h-5" fill={fav ? "#A16BF7" : "none"} stroke={fav ? "#A16BF7" : "currentColor"} />
          </button>
        )}
      </div>

      {/* 2. Media */}
      <div className="relative mb-3">
        <AvatarMedia
          avatar={avatar}
          peerAvatars={peerAvatars}
          aspectClass="aspect-[4/5]"
          roundedClass="rounded-xl"
          monogramClass="text-[72px]"
          generating={generating}
        />
        {!isDraft && !generating && !readOnly && (
          <div className="absolute top-2 right-2">
            {avatar.foto_url && onRegeneratePhoto ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    data-testid="avatar-card-photo-menu"
                    onClick={(e) => e.stopPropagation()}
                    aria-label="Opções do retrato"
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-brand-bg/70 backdrop-blur text-brand-ink2 hover:text-brand-rose transition-colors"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-brand-surface border-brand-line">
                  <DropdownMenuItem
                    data-testid="avatar-card-regenerate"
                    onClick={(e) => { e.stopPropagation(); onRegeneratePhoto(avatar); }}
                    className="text-brand-ink focus:bg-brand-elevated cursor-pointer"
                  >
                    <RefreshCcw className="w-3.5 h-3.5 mr-2 text-brand-rose" /> Regenerar retrato
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              !avatar.foto_url && onGeneratePhoto && (
                <button
                  type="button"
                  data-testid="avatar-card-generate-photo"
                  onClick={(e) => { e.stopPropagation(); onGeneratePhoto(avatar); }}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-brand-bg/70 backdrop-blur border border-brand-rose/40 text-brand-rose text-[11px] font-ui hover:bg-brand-rose hover:text-white transition-colors"
                >
                  <Sparkles className="w-3 h-3" /> Gerar retrato
                </button>
              )
            )}
          </div>
        )}
      </div>

      {/* 3. Public description */}
      <div className="flex items-center gap-1.5 mb-0.5">
        <h3 className="font-display text-xl text-brand-ink truncate" data-testid="avatar-card-publico">
          {publicDescription}
        </h3>
        {!isDraft && (
          <span className="shrink-0 w-4 h-4 rounded-full bg-brand-rose flex items-center justify-center" title="Avatar completo">
            <Check className="w-2.5 h-2.5 text-white" />
          </span>
        )}
      </div>
      {isDraft ? (
        <p className="text-[13px] text-brand-ink2 leading-snug line-clamp-2">
          Rascunho em construção. Continue a entrevista para completar.
        </p>
      ) : (
        <>
          {/* 4. Labelled rows */}
          <div className="space-y-1.5 mb-3">
            <LabelRow label="objetivo" value={objetivo} testid="avatar-card-objetivo" />
            <LabelRow label="dor" value={dor} testid="avatar-card-dor" />
            {gatilho && <LabelRow label="gatilho" value={gatilho} testid="avatar-card-gatilho" />}
            {consciencia && (
              <LabelRow label="consciência" value={consciencia} valueClass="text-brand-rose" testid="avatar-card-consciencia" />
            )}
          </div>

          {/* 6. Footer: location/income + escrever copy */}
          <div className="mt-auto flex items-center justify-between gap-2 pt-1">
            <span className="text-[11.5px] text-brand-ink3 font-ui truncate" data-testid="avatar-card-footer">
              {footer}
            </span>
            {!selectable && !readOnly && onWriteCopy && (
              <button
                type="button"
                data-testid="avatar-card-write-copy"
                onClick={(e) => { e.stopPropagation(); onWriteCopy(avatar); }}
                className="shrink-0 inline-flex items-center gap-1 px-3.5 py-2 rounded-full border border-brand-rose text-brand-rose text-[12px] font-ui hover:bg-brand-rose hover:text-white transition-colors min-h-[44px]"
              >
                escrever copy <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </>
      )}
    </Wrapper>
  );
}

function LabelRow({ label, value, valueClass = "text-brand-ink", testid }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline gap-2" data-testid={testid}>
      <span className="text-[10px] uppercase tracking-wide text-brand-ink3 font-ui shrink-0 w-[70px]">
        {label}
      </span>
      <span className={`text-[12.5px] truncate ${valueClass}`}>{value}</span>
    </div>
  );
}
